<template>
	<button
		:class="btnStyles"
		:disabled="disabled || loading"
		:aria-busy="loading"
	>
		<LoadingIcon
			v-if="loading"
			size="size-4"
			color="border-current"
		/>
		<slot
			v-else
			name="leftIcon"
		/>
		<slot />
		<slot name="rightIcon" />
	</button>
</template>

<script setup>
import { computed, toRefs, useSlots } from 'vue';
import LoadingIcon from '../../icons/loading-icon.vue';

// The press state can't use outline: app.css resets `* { outline: none }` outside any layer, which beats
// every outline utility (EN-897).
const defaultButtonStyles = [
	'text-p-base font-semibold flex justify-center items-center gap-1 disabled:cursor-default',
	'enabled:active:translate-y-px enabled:active:brightness-90',
].join(' ');

const props = defineProps({
	variant: {
		type: String,
		default: 'primary',
		validator(value) {
			return ['primary', 'secondary', 'tertiary', 'quaternary'].includes(value);
		}
	},
	size: {
		type: String,
		default: 'big',
		validator(value) {
			return ['small', 'medium', 'big', 'extraBig'].includes(value);
		}
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	style: {
		type: String,
		default: 'normal',
		validator(value) {
			return ['normal', 'circle'].includes(value);
		}
	}
});

const { size, variant, style } = toRefs(props);
const buttonSlots = useSlots();

const sizeOptions = {
	small: [
		'px-2 py-1 h-7',
	],
	medium: [
		'px-2.5 py-1.5 h-8',
	],
	big: [
		'px-3 py-2 h-9',
	],
	extraBig: [
		'px-3.5 py-2.5 h-10',
	]
};

// A loading button is disabled too, but keeps its variant colours (aria-busy) so the spinner reads as
// "working" rather than as the grey "not available" state.
const variationsOptions = {
	primary: [
		'text-white bg-won-green',
		'hover:bg-won-green-dark',
		'disabled:not-aria-busy:bg-[#E5E5E5] disabled:not-aria-busy:text-[#A1A1AA] disabled:outline-hidden'
	],
	secondary: [
		'text-won-green bg-white',
		'hover:bg-won-green-light border border-won-green',
		'disabled:not-aria-busy:bg-[#E5E5E5] disabled:not-aria-busy:text-[#A1A1AA] disabled:outline-hidden disabled:not-aria-busy:border-[#E5E5E5]'
	],
	tertiary: [
		'text-won-black bg-white',
		'hover:bg-[#F5F5F5] border border-[#A3A3A3]',
		'disabled:not-aria-busy:bg-[#E5E5E5] disabled:not-aria-busy:text-[#A1A1AA] disabled:outline-hidden disabled:not-aria-busy:border-[#E5E5E5]'
	],
	quaternary: [
		'text-won-black bg-white',
		'hover:bg-[#F5F5F5]',
		'active:bg-white',
		'disabled:not-aria-busy:bg-[#E5E5E5] disabled:not-aria-busy:text-[#A1A1AA] disabled:outline-hidden'
	],
};

const btnStyles = computed(() => {
	const baseStyles = [
		defaultButtonStyles,
		...variationsOptions[variant.value],
	];

	if (style.value === 'circle') {
		baseStyles.push('rounded-full');

		if ((buttonSlots.rightIcon != null || buttonSlots.leftIcon != null) && buttonSlots.default == null) {
			switch (size.value) {
				case 'small':
					baseStyles.push('size-7');
					break;
				case 'medium':
					baseStyles.push('size-8');
					break;
				case 'big':
					baseStyles.push('size-9');
					break;
				case 'extraBig':
					baseStyles.push('size-10');
					break;
			}
		} else {
			baseStyles.push(...sizeOptions[size.value]);
		}
	} else {
		if (size.value === 'small') {
			baseStyles.push('rounded');
		} else {
			baseStyles.push('rounded-md');
		}

		baseStyles.push(...sizeOptions[size.value]);
	}

	return baseStyles;
});
</script>
